import React from 'react';
import { Crown, Star, Shield, Phone, Eye, BarChart3 } from 'lucide-react';

interface MembershipTierBadgeProps {
  tier: 'standard' | 'premium' | null;
  size?: 'sm' | 'md' | 'lg';
  showFeatures?: boolean;
  className?: string;
}

const MembershipTierBadge: React.FC<MembershipTierBadgeProps> = ({
  tier,
  size = 'md',
  showFeatures = false,
  className = ''
}) => {
  if (!tier) return null;

  const tierConfig = {
    standard: {
      name: 'Standard',
      icon: Star,
      color: 'bg-gray-100 text-gray-700 border-gray-300',
      iconColor: 'text-gray-600',
      features: ['Up to 3 services', 'Basic profile visibility', 'Email support']
    },
    premium: {
      name: 'Premium',
      icon: Crown,
      color: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white border-transparent',
      iconColor: 'text-white',
      features: ['Unlimited services', 'Verified badge', 'Phone visibility', 'Priority support', 'Enhanced visibility']
    }
  };

  const config = tierConfig[tier];
  const Icon = config.icon;

  const badgeStyle = tier === 'premium'
    ? {
        background: 'linear-gradient(135deg, #EF9F27 0%, #FAC775 100%)',
        color: '#ffffff',
        border: 'none',
      }
    : {
        background: '#f3f4f6',
        color: '#374151',
        border: '1px solid #d1d5db',
      };

  const iconStyle = tier === 'premium' ? { color: '#ffffff' } : { color: '#4b5563' };

  // Size configurations
  const sizeConfigs = {
    sm: {
      icon: 14,
      text: 'text-xs',
      padding: 'px-2 py-1',
      gap: 'gap-1'
    },
    md: {
      icon: 16,
      text: 'text-sm',
      padding: 'px-3 py-1.5',
      gap: 'gap-2'
    },
    lg: {
      icon: 20,
      text: 'text-base',
      padding: 'px-4 py-2',
      gap: 'gap-2'
    }
  };

  const sizeConfig = sizeConfigs[size];

  return (
    <div className={className}>
      <div 
        style={badgeStyle}
        className={`inline-flex items-center ${sizeConfig.gap} ${sizeConfig.padding} rounded-full font-medium ${sizeConfig.text}`}
      >
        <Icon size={sizeConfig.icon} style={iconStyle} />
        <span>{config.name}</span>
      </div>
      
      {showFeatures && (
        <div className="mt-3 space-y-2">
          {config.features.map((feature, index) => (
            <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
              {tier === 'premium' && feature === 'Verified badge' && <Shield size={14} className="text-blue-600" />}
              {tier === 'premium' && feature === 'Phone visibility' && <Phone size={14} className="text-green-600" />}
              {tier === 'premium' && feature === 'Enhanced visibility' && <Eye size={14} className="text-purple-600" />}
              {tier === 'premium' && feature === 'Priority support' && <BarChart3 size={14} className="text-orange-600" />}
              {(tier === 'standard' || !['Verified badge', 'Phone visibility', 'Enhanced visibility', 'Priority support'].includes(feature)) && (
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              )}
              <span>{feature}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MembershipTierBadge; 